import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, CircularProgress, MenuItem, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierBillService } from '../../services/supplierBill.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { AuthService } from '../../services/auth.service';
import { PurchaseOrder } from '../../interfaces/procurement/purchaseOrder.interface';
import { GoodsReceiptLine } from '../../interfaces/procurement/goodsReceipt.interface';
import { useToast } from '../../context/ToastContext';

interface GRNLineWithGRN {
  grnLine: GoodsReceiptLine;
  grnId: string;
  grnNumber: string;
}

const today = () => new Date().toISOString().slice(0, 10);
const todayPlus30 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

export const SupplierBillCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedPoId = searchParams.get('purchaseOrderId');

  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [grnLines, setGrnLines] = useState<GRNLineWithGRN[]>([]);
  // True when the PO has POSTED GRN lines, even if all are already billed (drives the empty-state text).
  const [hadPostedLines, setHadPostedLines] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  // Supplier-invoiced unit price per GRN line; defaults to the GRN/PO price.
  const [lineUnitPrices, setLineUnitPrices] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState('');
  const [billDate, setBillDate] = useState(today());
  const [dueDate, setDueDate] = useState(todayPlus30());
  const [taxAmount, setTaxAmount] = useState(0);

  // Prorated tax calculation
  const selectedLinesValue = useMemo(() => {
    return grnLines
      .filter(l => selectedLineIds.has(l.grnLine.id))
      .reduce((sum, l) => sum + l.grnLine.lineValue, 0);
  }, [grnLines, selectedLineIds]);

  const proratedTax = useMemo(() => {
    if (!selectedPO || selectedPO.subtotal === 0) return 0;
    return Math.round((selectedLinesValue / selectedPO.subtotal) * selectedPO.taxAmount * 100) / 100;
  }, [selectedLinesValue, selectedPO]);

  useEffect(() => {
    if (!lockedPoId) {
      // Load selectable POs: ORDERED, PARTIALLY_RECEIVED, RECEIVED, CLOSED
      Promise.all([
        PurchaseOrderService.listByInstitution(0, 500, 'ORDERED'),
        PurchaseOrderService.listByInstitution(0, 500, 'PARTIALLY_RECEIVED'),
        PurchaseOrderService.listByInstitution(0, 500, 'RECEIVED'),
        PurchaseOrderService.listByInstitution(0, 500, 'CLOSED'),
      ])
        .then(([o, p, r, c]) => setPos([...o.content, ...p.content, ...r.content, ...c.content]))
        .catch(e => toast.showApiError(e, 'Failed to load purchase orders'))
        .finally(() => setLoading(false));
    } else {
      PurchaseOrderService.getById(lockedPoId)
        .then(po => {
          setSelectedPO(po);
          return loadGRNLines(po.id);
        })
        .catch(e => toast.showApiError(e, 'Failed to load data'))
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGRNLines = async (poId: string) => {
    const grnRes = await GoodsReceiptService.listByInstitution(0, 500);
    const postedSummaries = grnRes.content.filter(g => g.purchaseOrderId === poId && g.status === 'POSTED');
    // The list endpoint omits nested lines; fetch each GRN's detail to get them.
    const postedGRNs = await Promise.all(postedSummaries.map(g => GoodsReceiptService.getById(g.id)));
    const allLines: GRNLineWithGRN[] = postedGRNs.flatMap(g =>
      g.lines.map(l => ({ grnLine: l, grnId: g.id, grnNumber: g.grnNumber }))
    );
    // The backend rejects a GRN line already on any non-REVERSED bill ("already covered by an
    // active supplier bill"), so don't offer those. Bill summaries omit their lines, so read each
    // live bill's detail to learn which GRN lines it covers.
    const billsRes = await SupplierBillService.listByPurchaseOrder(poId, 0, 500);
    const liveBills = billsRes.content.filter(b => b.status !== 'REVERSED');
    const liveBillDetails = await Promise.all(liveBills.map(b => SupplierBillService.getById(b.id)));
    const billedGrnLineIds = new Set(liveBillDetails.flatMap(b => b.lines.map(l => l.goodsReceiptLineId)));
    const lines = allLines.filter(l => !billedGrnLineIds.has(l.grnLine.id));
    setHadPostedLines(allLines.length > 0);
    setGrnLines(lines);
    return lines;
  };

  const handlePOChange = async (poId: string) => {
    const po = pos.find(p => p.id === poId);
    if (!po) return;
    setSelectedPO(po);
    setGrnLines([]);
    setHadPostedLines(false);
    setSelectedLineIds(new Set());
    setLoading(true);
    try {
      await loadGRNLines(po.id);
    } catch (e) {
      toast.showApiError(e, 'Failed to load GRN lines');
    } finally {
      setLoading(false);
    }
  };

  const toggleLine = (lineId: string) => {
    setSelectedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!selectedPO) { toast.showApiError(null, 'Select a Purchase Order'); return; }
    if (selectedLineIds.size === 0) { toast.showApiError(null, 'Select at least one GRN line'); return; }

    const selectedLines = grnLines.filter(l => selectedLineIds.has(l.grnLine.id));
    const priceOf = (l: GRNLineWithGRN) => lineUnitPrices[l.grnLine.id] ?? l.grnLine.unitPrice;
    if (selectedLines.some(l => !(priceOf(l) > 0))) {
      toast.showApiError(null, 'Enter a positive bill unit price for every selected line');
      return;
    }

    setSaving(true);
    try {
      const dto = {
        purchaseOrderId: selectedPO.id,
        supplierInvoiceRef: supplierInvoiceRef || undefined,
        billDate,
        dueDate,
        taxAmount: taxAmount || undefined,
        institutionId: AuthService.getSelectedInstitution()!!,
        lines: selectedLines.map(l => ({
          goodsReceiptLineId: l.grnLine.id,
          quantity: l.grnLine.receivedQty,
          unitPrice: lineUnitPrices[l.grnLine.id] ?? l.grnLine.unitPrice,
        })),
      };
      const bill = await SupplierBillService.create(dto);
      toast.showSuccess('Supplier Bill created');
      navigate(`/procurement/supplier-bills/${bill.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create Supplier Bill');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>New Supplier Bill</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Purchase Order"
            value={selectedPO?.id ?? ''}
            onChange={e => handlePOChange(e.target.value)}
            disabled={!!lockedPoId}
          >
            {lockedPoId && selectedPO ? (
              <MenuItem value={selectedPO.id}>{selectedPO.poNumber}</MenuItem>
            ) : (
              pos.map(po => (
                <MenuItem key={po.id} value={po.id}>{po.poNumber}</MenuItem>
              ))
            )}
          </TextField>

          <TextField
            label="Supplier Invoice Ref"
            value={supplierInvoiceRef}
            onChange={e => setSupplierInvoiceRef(e.target.value)}
          />

          <TextField
            label="Bill Date"
            type="date"
            value={billDate}
            onChange={e => setBillDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <Box>
            <TextField
              label="Tax Amount"
              type="number"
              value={taxAmount}
              onChange={e => setTaxAmount(Number(e.target.value))}
              inputProps={{ step: 0.01, min: 0 }}
            />
            {selectedPO && selectedLineIds.size > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Prorated from PO tax: {proratedTax} (selected lines value {selectedLinesValue} / PO subtotal {selectedPO.subtotal} × PO tax {selectedPO.taxAmount})
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {selectedPO && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>POSTED GRN Lines</Typography>
          {grnLines.length === 0 ? (
            <Typography color="text.secondary">
              {hadPostedLines
                ? 'Every received line on this PO is already on an active supplier bill. Nothing is left to bill.'
                : 'No POSTED GRN lines found for this PO.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>GRN Line ID</TableCell>
                  <TableCell>GRN Number</TableCell>
                  <TableCell>Item ID</TableCell>
                  <TableCell>Received Qty</TableCell>
                  <TableCell>PO Unit Price</TableCell>
                  <TableCell>Bill Unit Price</TableCell>
                  <TableCell>Line Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grnLines.map(({ grnLine, grnNumber }) => {
                  const selected = selectedLineIds.has(grnLine.id);
                  const rawPrice = lineUnitPrices[grnLine.id] ?? grnLine.unitPrice;
                  const billPrice = Number.isFinite(rawPrice) ? rawPrice : NaN;
                  return (
                  <TableRow key={grnLine.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={() => toggleLine(grnLine.id)}
                      />
                    </TableCell>
                    <TableCell>{grnLine.id}</TableCell>
                    <TableCell>{grnNumber}</TableCell>
                    <TableCell>{grnLine.itemId}</TableCell>
                    <TableCell>{grnLine.receivedQty}</TableCell>
                    <TableCell>{grnLine.unitPrice}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        error={selected && !(billPrice > 0)}
                        value={Number.isFinite(billPrice) ? billPrice : ''}
                        disabled={!selected}
                        onChange={e => {
                          const v = e.target.value;
                          setLineUnitPrices(prev => ({
                            ...prev,
                            [grnLine.id]: v === '' ? NaN : Number(v),
                          }));
                        }}
                        inputProps={{ step: 0.0001, min: 0, 'aria-label': `bill-unit-price-${grnLine.id}` }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>{Number.isFinite(billPrice) ? (billPrice * grnLine.receivedQty).toFixed(4) : '—'}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Create Bill'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierBillCreate;
